import { useState, useEffect } from "react";
import { useAppContext } from "@/App";
import api from "@/lib/api";
import { Play, Square, Clock } from "lucide-react";

export default function WorkdayButton({ role }) {
  const { auth, session } = useAppContext();
  const [active, setActive] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const saved = sessionStorage.getItem(`workday-${session?.hubId}-${role}`);
    if (saved) {
      const ts = new Date(saved);
      setActive(true);
      setStartTime(ts);
    }
  }, [session?.hubId, role]);

  useEffect(() => {
    if (!active || !startTime) return;
    const timer = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime.getTime()) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [active, startTime]);

  const formatElapsed = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const handleStart = async () => {
    const now = new Date();
    setActive(true);
    setStartTime(now);
    setElapsed(0);
    sessionStorage.setItem(`workday-${session?.hubId}-${role}`, now.toISOString());

    try {
      await api.logActivity(auth?.jwt, {
        hub_id: session?.hubId,
        action: "SHIFT_START",
        role,
        display_name: session?.userName || auth?.user?.email || "",
        detail: `Started workday as ${role}`,
      });
    } catch (e) {
      void 0;
    }
  };

  const handleStop = async () => {
    setActive(false);
    sessionStorage.removeItem(`workday-${session?.hubId}-${role}`);

    try {
      await api.logActivity(auth?.jwt, {
        hub_id: session?.hubId,
        action: "SHIFT_END",
        role,
        display_name: session?.userName || auth?.user?.email || "",
        detail: `Ended workday as ${role} (${formatElapsed(elapsed)})`,
      });
    } catch (e) {
      void 0;
    }
    setElapsed(0);
    setStartTime(null);
  };

  if (!active) {
    return (
      <button
        data-testid="start-workday-btn"
        onClick={handleStart}
        className="flex items-center gap-2 px-4 py-2 bg-lime-500 hover:bg-lime-400 text-slate-950 rounded-xl text-[10px] font-black uppercase tracking-wider shadow-sm transition-all active:scale-[0.97]"
      >
        <Play className="w-3.5 h-3.5" fill="currentColor" /> Start Workday
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2" data-testid="workday-active">
      <div className="flex items-center gap-1.5 px-3 py-2 bg-lime-50 border border-lime-300 rounded-xl">
        <Clock className="w-3.5 h-3.5 text-lime-600 animate-pulse" />
        <span className="text-xs font-mono font-bold text-lime-700 tabular-nums">{formatElapsed(elapsed)}</span>
      </div>
      <button
        data-testid="stop-workday-btn"
        onClick={handleStop}
        className="flex items-center gap-1.5 px-3 py-2 bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all active:scale-[0.97]"
      >
        <Square className="w-3 h-3" fill="currentColor" /> End
      </button>
    </div>
  );
}
